import React, { CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import styles from './ProgressBar.module.scss';

export enum ProgressBarVariant {
  Default = 'default',
  Slim = 'slim',
}

interface ProgressBarProps {
  progress: number;
  variant?: ProgressBarVariant;
  currentAmount?: string;
  totalAmount?: string;
}

const ProgressBar = ({
  progress,
  variant = ProgressBarVariant.Default,
  currentAmount,
  totalAmount,
}: ProgressBarProps) => {
  const isSlim = variant === ProgressBarVariant.Slim;
  const totalAmountRef = useRef<HTMLSpanElement>(null);
  const currentAmountRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentAmountLeft, setCurrentAmountLeft] = useState('0px');

  useLayoutEffect(() => {
    if (totalAmountRef.current && currentAmountRef.current && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const totalAmountWidth = totalAmountRef.current.offsetWidth;
      const currentAmountWidth = currentAmountRef.current.offsetWidth;

      const desiredLeft = (progress / 100) * containerWidth;

      const maxLeft = containerWidth - totalAmountWidth - currentAmountWidth;

      setCurrentAmountLeft(`${Math.min(desiredLeft, maxLeft)}px`);
    }
  }, [progress, currentAmount, totalAmount]);

  const shouldShowLabels = currentAmount || totalAmount;

  return (
    <div className={styles.container}>
      <div className={clsx(styles.background, { [styles.slim]: isSlim })}>
        <div className={clsx(styles.progress, { [styles.slim]: isSlim })} style={{ width: `${progress}%` }} />
      </div>
      {shouldShowLabels && (
        <div className={styles.labels} ref={containerRef}>
          {currentAmount && (
            <span
              ref={currentAmountRef}
              className={styles.currentAmount}
              style={{ left: currentAmountLeft } as CSSProperties}
            >
              {currentAmount}
            </span>
          )}
          {totalAmount && (
            <span ref={totalAmountRef} className={styles.totalAmount}>
              {totalAmount}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressBar;
